import type { ChatPartRendererProps } from '@mui/x-chat/headless';
import { useState, useSyncExternalStore } from 'react';

import { isDefined } from 'common';

import { AgenticChatActivityPart } from './Part';
import { AgenticChatActivityWorkSummary } from './WorkSummary';
import {
  type AgenticChatActivityMessagePart,
  isAgenticChatActivityPart,
  isAgenticChatActivityPartVisible,
} from './isVisiblePart';
import { createAgenticChatActivityTimer } from './timer';

/** Groups work until the next response part so the visible answer keeps its place. */
export function AgenticChatActivityWorkGroup(
  props: ChatPartRendererProps<AgenticChatActivityMessagePart>
) {
  const { message, index } = props;
  const parts: AgenticChatActivityMessagePart[] = [];
  for (const part of message.parts.slice(index)) {
    if (!isAgenticChatActivityPart(part)) {
      break;
    }
    parts.push(part);
  }
  const isRunning = message.status === 'streaming' && index + parts.length === message.parts.length;
  let latestReasoningOffset = -1;
  for (let offset = parts.length - 1; offset >= 0; offset -= 1) {
    const part = parts[offset];
    if (
      isDefined(part) &&
      part.type === 'reasoning' &&
      isAgenticChatActivityPartVisible({ part, message, index: index + offset })
    ) {
      latestReasoningOffset = offset;
      break;
    }
  }
  const latestReasoning = parts[latestReasoningOffset];
  const [mountSource] = useState<'Live' | 'History'>(isRunning ? 'Live' : 'History');
  const reasoningPresentation =
    (isRunning || mountSource === 'Live') &&
    index + parts.length === message.parts.length &&
    isDefined(latestReasoning) &&
    latestReasoning.type === 'reasoning'
      ? 'LivePreview'
      : 'History';
  const [livePreviewFocus, setLivePreviewFocus] = useState<'Focused' | 'Blurred'>('Blurred');
  const timerState =
    isRunning &&
    isDefined(message.metadata) &&
    'runStartedAtMilliseconds' in message.metadata &&
    typeof message.metadata.runStartedAtMilliseconds === 'number'
      ? 'Running'
      : 'Stopped';
  const [workTimer] = useState(() =>
    createAgenticChatActivityTimer({ intervalMilliseconds: 1_000 })
  );
  const nowMilliseconds = useSyncExternalStore(
    timerState === 'Running' ? workTimer.subscribe : workTimer.subscribeWithoutStartingTimer,
    workTimer.getSnapshot
  );
  const workParts = parts.flatMap((part, offset) =>
    reasoningPresentation === 'LivePreview' && offset === latestReasoningOffset
      ? []
      : [{ part, offset }]
  );
  const hasVisibleWork = workParts.some(({ part, offset }) =>
    isAgenticChatActivityPartVisible({ part, message, index: index + offset })
  );
  if (!hasVisibleWork && reasoningPresentation !== 'LivePreview') {
    return null;
  }

  return (
    <>
      {hasVisibleWork && (
        <AgenticChatActivityWorkSummary
          {...props}
          workParts={workParts}
          runState={isRunning ? 'Running' : 'Complete'}
          nowMilliseconds={nowMilliseconds}
          focus={
            livePreviewFocus === 'Focused' && reasoningPresentation !== 'LivePreview'
              ? 'Restore'
              : 'Preserve'
          }
          onFocusChange={() => setLivePreviewFocus('Blurred')}
        />
      )}
      {reasoningPresentation === 'LivePreview' &&
        isDefined(latestReasoning) &&
        latestReasoning.type === 'reasoning' && (
          <AgenticChatActivityPart
            {...props}
            onFocusChange={(focus) => setLivePreviewFocus(focus)}
            key={`preview-${index}`}
            part={latestReasoning}
            index={index + latestReasoningOffset}
            reasoningPresentation="LivePreview"
          />
        )}
    </>
  );
}
